import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DeckCard } from "../lib/types";

/**
 * Full profile view — ported from ProfileDetailView.swift. Opened by tapping a
 * discover card, or as a read-only preview of your own profile (the eye icon).
 */
export default function ProfileDetail({
  profile, isPreview = false, actionsFlush = false, onClose, onLike, onPass, onReport,
}: {
  profile: DeckCard;
  isPreview?: boolean;
  /** No floating tab pill in this context (e.g. inside a Relay room) — drop
   * the ✕/♥ bar all the way to the bottom of the screen. */
  actionsFlush?: boolean;
  onClose: () => void;
  onLike?: () => void;
  onPass?: () => void;
  onReport?: () => void;
}) {
  const [photoIdx, setPhotoIdx] = useState(0);
  const photos = profile.photoURLs;

  // Swipe through photos like the Discover card — a horizontal drag flips
  // back/forward; taps on the left/right halves still work too.
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // Portal to <body>: escapes the app's scroll container, whose iOS momentum
  // scrolling otherwise traps this overlay beneath the tab bar.
  return createPortal(
    <div className="detail-backdrop" role="dialog" aria-modal="true" aria-label={`${profile.displayName}'s profile`} onClick={onClose}>
      <div className="detail-card" onClick={(e) => e.stopPropagation()}>
        <button className="detail-x" aria-label="Close" onClick={onClose}>✕</button>

        <div
          className="detail-photo"
          onTouchStart={(e) => {
            touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            swiped.current = false;
          }}
          onTouchMove={(e) => {
            if (!touchStart.current) return;
            const dx = e.touches[0].clientX - touchStart.current.x;
            const dy = e.touches[0].clientY - touchStart.current.y;
            // Horizontal intent only — vertical drags keep scrolling the card.
            if (Math.abs(dx) > 35 && Math.abs(dx) > Math.abs(dy) * 1.4) swiped.current = true;
          }}
          onTouchEnd={(e) => {
            const start = touchStart.current;
            touchStart.current = null;
            if (!start || !swiped.current || photos.length < 2) return;
            const dx = e.changedTouches[0].clientX - start.x;
            setPhotoIdx((i) => Math.min(Math.max(0, dx < 0 ? i + 1 : i - 1), photos.length - 1));
          }}
          onClick={(e) => {
            // A swipe already advanced the photo — swallow the trailing click.
            if (swiped.current) { swiped.current = false; return; }
            if (photos.length < 2) return;
            const { left, width } = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const back = e.clientX - left < width / 2;
            setPhotoIdx((i) => Math.min(Math.max(0, back ? i - 1 : i + 1), photos.length - 1));
          }}
        >
          {photos.length ? (
            <img src={photos[photoIdx]} alt="" draggable={false} />
          ) : (
            <div className="deck-photo-empty">☺</div>
          )}
          {photos.length > 1 && (
            <div className="photo-dots">
              {photos.map((_, i) => <span key={i} className={"dot" + (i === photoIdx ? " dot-on" : "")} />)}
            </div>
          )}
        </div>

        <div className="detail-body">
          <div className="detail-name">
            {profile.displayName || "Your name"} <span className="detail-age">{profile.age || ""}</span>
          </div>
          {profile.headline && <div className="detail-headline">{profile.headline}</div>}
          <div className="muted" style={{ fontSize: 14, marginTop: 4 }}>
            📍 {profile.locationName
              ? `${profile.locationName} · ${profile.distanceMiles} mi`
              : isPreview ? "Your area" : `${profile.distanceMiles} miles away`}
          </div>

          {profile.bio && (
            <>
              <div className="detail-label">About me</div>
              <p style={{ margin: "4px 0 0" }}>{profile.bio}</p>
            </>
          )}

          {profile.prompts.filter((p) => p.answer).map((p) => (
            <div className="prompt-card" key={p.prompt}>
              <div className="prompt-q" style={{ color: "var(--accent)", fontWeight: 700 }}>{p.prompt}</div>
              <div className="prompt-a">{p.answer}</div>
            </div>
          ))}
          {onReport && (
            <button className="btn-ghost-sm" onClick={() => { onClose(); onReport(); }}>
              Block or report {profile.displayName}
            </button>
          )}
        </div>

        {!isPreview && (
          <div className={"detail-actions" + (actionsFlush ? " detail-actions-flush" : "")}>
            <button className="decide decide-pass" aria-label="Pass" onClick={() => { onClose(); onPass?.(); }}>✕</button>
            <button className="decide decide-like" aria-label="Like" onClick={() => { onClose(); onLike?.(); }}>♥</button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
